import React, { useState } from 'react';
import MuiButton from '@material-ui/core/Button';
import { getCookieValue } from "../utils/getCookieValue";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usePage } from "@inertiajs/inertia-react";
import styled from "styled-components";
import dayjs from 'dayjs';
import { Inertia } from '@inertiajs/inertia';
import DeleteIcon from '@material-ui/icons/Delete';
import CheckIcon from '@material-ui/icons/Check';
import CrossIcon from '@material-ui/icons/Close';
import {Trans} from "../utils/i18n";
import {Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Tab, Tabs} from "@mui/material";

const SytledTextreaWrapper = styled.div`
    border: 1px solid rgba(0, 0, 0, .32);
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.25);
    margin-top: 15px;
    position: relative;
`;

const StyledTextarea = styled.textarea`
    width: 100%;
    resize: none;
    border: none;
    padding: 15px 15px 0;

    &:focus {
      outline: none;
    }
`;

const StyledSendButton = styled.div`
    position: absolute;
    bottom: 15px;
    right: 15px;
`;

const StyledMessages = styled.ul`
    list-style-type: none;
    margin: 0;
    padding: 0;
    margin-top: 35px;
`;

const StyledMessage = styled.li`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 0 10px;
    position: relative;
`;

const StyledName = styled.p`
    font-weight: bold;
    font-size: 18px;
    flex: 1;
    margin: 0;
`;

const CompanyName = styled.p`
    color: ${props => props.theme.primary};
    opacity: .7;
    font-size: 14px;
    margin: 0;
`;

const StyledDate = styled.time`
    font-weight: bold;
    margin: 0;
    font-size: 14px;
`;

const StyledContent = styled.p`
    flex-basis: 100%;
    margin: 0;
    font-weight: normal;
    margin-top: 20px;
    white-space: pre-line;
`;

const StyledHr = styled.hr`
    flex-basis: 100%;
    margin: 15px 0;
`;

const LoadMoreWrapper = styled.div`
    text-align: center;
`;

const StyledButton = styled.button`
    text-align: center;
    padding: 5px 10px;
    background-color: ${props => props.theme.secondary};
    color: ${props => props.theme.white};
    display: inline-block;
    border: 0;
    outline: none;
    cursor: pointer;
`;

const ActionsWrapper = styled.div`
    position: absolute;
    right: 0;
    bottom: 15px;
    color: ${props => props.theme.gray};
    opacity: .7;
`;

const CardNameWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
    hyphens: auto;
    flex-wrap: wrap;
`;
const IconWrapper = styled.div`
    margin-right: 5px;
`;

const mutateChat = ({ content, order_id }) =>
    fetch(route('chat.postMessage'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-XSRF-TOKEN': getCookieValue('XSRF-TOKEN'),
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({content, order_id}),
    });

const deleteChat = ({ orderNoteId }) =>
    fetch(route('chat.delete', {orderNote: orderNoteId}), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-XSRF-TOKEN': getCookieValue('XSRF-TOKEN'),
            'X-Requested-With': 'XMLHttpRequest',
        },
    });

export function Chat() {
    const { props: { user: authUser, permissions, }} = usePage();

    const perPage = 5;
    const [page, setPage] = useState(1);
    const [type, setType] = useState('OrderNote');
    const [deleteChatItem, setDeleteChatItem] = useState(null);

    const fetchChat = () => {
        return fetch(route('chat.index') + `?page=${page}&order_id=${order.order_id}&type=${type}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'X-XSRF-TOKEN': getCookieValue('XSRF-TOKEN'),
                'X-Requested-With': 'XMLHttpRequest',
            },
        })
        .then(response => response.json());
    };

    const queryClient = useQueryClient();
    const { order } = usePage().props;
    const { data, isLoading } = useQuery({
        queryKey: ['chat', order.order_id, type],
        queryFn: fetchChat
    });
    const [message, setMessage] = useState("");

    const mutation = useMutation({
        mutationFn: mutateChat,
        onSuccess: () => {
            queryClient.invalidateQueries(['chat', order.order_id])
            setMessage('');
        },
    });

    const deleteMutation = useMutation({
        mutationFn: deleteChat,
        onSuccess: () => {
            queryClient.invalidateQueries(['chat', order.order_id])
        },
    });

    return isLoading ? (
        <div>Loading...</div>
    ) : (
        <>
            <Tabs
                value={type}
                onChange={(event, newValue) => setType(newValue)}
                aria-label="basic tabs example"
            >
                <Tab
                    label={<Trans i18nKey={"queueDetail.orderNote"}>OrderNote</Trans>}
                    id={"OrderNote"}
                    value={'OrderNote'}
                    style={{ paddingLeft: 10, paddingRight: 10 }}
                />

                <Tab
                    label={<Trans i18nKey={"queueDetail.messageLog"}>MessageLog</Trans>}
                    id={"MessageLog"}
                    value={'MessageLog'}
                />
            </Tabs>

            {
                type === 'OrderNote' && permissions.includes("ordernotes_create") &&
                <SytledTextreaWrapper>
                    <StyledTextarea
                        disabled={mutation.isLoading}
                        value={message}
                        onChange={
                            (event) => {
                                setMessage(event.target.value);
                            }
                        }
                        onKeyDown={(event) => {
                            if ((event.ctrlKey || event.metaKey) && event.keyCode === 13) {
                                mutation.mutateAsync({ content: message, order_id: order.order_id })
                            }
                        }}
                        rows={7}
                    />
                    <StyledSendButton>
                        <StyledButton
                            onClick={() => mutation.mutateAsync({ content: message, order_id: order.order_id })}
                            disabled={mutation.isLoading}
                        >
                            { mutation.isLoading ? <Trans i18nKey="common.sending">Sending...</Trans> : <Trans i18nKey="common.send">Send</Trans> }
                        </StyledButton>
                    </StyledSendButton>
                </SytledTextreaWrapper>
            }

            <StyledMessages>
                {type === 'OrderNote' && data?.filter((_, index) => index < perPage * page)
                    ?.map(({ id, user, order, content, timestamp }) => (
                        <StyledMessage key={id}>
                            <div>
                                <StyledName>
                                    {user?.name || <Trans i18nKey="common.system">System</Trans>}
                                </StyledName>
                                <CompanyName>{ user?.company?.name || '' }</CompanyName>
                            </div>
                            <StyledDate dateTime={timestamp}>{dayjs(timestamp).format('DD. MMMM YYYY HH:mm')}</StyledDate>
                            <StyledContent>{content}</StyledContent>
                            <ActionsWrapper>
                                {
                                    user?.id === authUser.id &&
                                    <DeleteIcon style={{ height: 30 }} onClick={() => setDeleteChatItem({ id, user, order, content, timestamp })}/>
                                }
                            </ActionsWrapper>
                            <StyledHr />
                        </StyledMessage>
                    ))}
                {type === 'MessageLog' && data?.filter((_, index) => index < perPage * page)
                    ?.map(({ id, messagetype, is_succeeded, error, created_at }) => (
                        <StyledMessage key={id}>
                            <CardNameWrapper>
                                <IconWrapper>
                                    {
                                        !!is_succeeded &&
                                        <CheckIcon style={{ color: 'green', verticalAlign: 'text-bottom', marginLeft: 5 }}/>
                                    }
                                    {
                                        !is_succeeded &&
                                        <CrossIcon style={{ color: 'red', verticalAlign: 'text-bottom', marginLeft: 5 }}/>
                                    }
                                </IconWrapper>
                                <StyledDate dateTime={created_at}>{dayjs(created_at).format('DD. MMMM YYYY HH:mm')}</StyledDate>
                            </CardNameWrapper>
                            <StyledContent>
                                <StyledName>
                                    { messagetype }
                                </StyledName>
                                {error}
                            </StyledContent>
                            <ActionsWrapper>
                                {/*{*/}
                                {/*    user.id === authUser.id &&*/}
                                {/*    <DeleteIcon onClick={() => deleteMutation.mutate({ orderNoteId: id })}/>*/}
                                {/*}*/}
                            </ActionsWrapper>
                            <StyledHr />
                        </StyledMessage>
                    ))}
            </StyledMessages>

            {perPage * page < data.length && <LoadMoreWrapper>
                <StyledButton onClick={() => setPage((prev) => prev + 1)}>
                <Trans i18nKey="common.show_more">Show more</Trans>
                </StyledButton>
            </LoadMoreWrapper>}

            {
                deleteChatItem &&
                <Dialog
                    open={true}
                    onClose={() => {
                        setDeleteChatItem(null);
                    }}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title"><Trans i18nKey={'queueDetails.chat.delete.title'}>Do you really want to delete this message?</Trans></DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            <Trans i18nKey={'queueDetails.chat.delete.title'}>Do you really want to delete this message?</Trans>
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => {
                                setDeleteChatItem(null);
                            }}
                            color={'primary'}
                            variant={'contained'}
                        >
                            <Trans i18nKey={'common.abort'}>Abort</Trans>
                        </Button>
                        <Button onClick={() => {
                                deleteMutation.mutateAsync({ orderNoteId: deleteChatItem.id }, {
                                    onSuccess: () => {
                                        setDeleteChatItem(null);
                                    }
                                })
                            }}
                            color={'secondary'}
                            variant={'contained'}
                        >
                            <Trans i18nKey={'common.delete'}>Delete</Trans>
                        </Button>
                    </DialogActions>
                </Dialog>
            }
        </>
    );
}

